import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = window.location.origin;

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    registration_enabled: true,
    capacity_cap_enabled: true,
    max_paid_users: 400,
    banner: {
      enabled: false,
      text: '',
      location: 'off',
      type: 'info'
    }
  });
  const [capacity, setCapacity] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/system/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data && response.data.status === 'success') {
        setSettings(response.data.settings);
        if (response.data.capacity) setCapacity(response.data.capacity);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      setMessage('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/admin/system/settings`, settings, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data && response.data.status === 'success') {
        setMessage('✅ Settings saved successfully');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      setMessage('❌ Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <h1 className="text-2xl font-bold text-white mb-6">System Settings</h1>

      {message && (
        <div className={`p-4 rounded ${message.includes('✅') ? 'bg-green-600' : 'bg-red-600'} text-white`}>
          {message}
        </div>
      )}

      {/* Registration Control */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4 text-white">Registration Control</h2>
        <div className="flex items-center space-x-4">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.registration_enabled}
              onChange={(e) => setSettings({
                ...settings,
                registration_enabled: e.target.checked
              })}
              className="w-6 h-6 rounded"
            />
            <span className="text-white">
              {settings.registration_enabled ? 'Registration Enabled' : 'Registration Disabled'}
            </span>
          </label>
        </div>
        <p className="text-sm text-gray-400 mt-2">
          Master switch. When off, nobody can create an account (paid or free).
        </p>
      </div>

      {/* Seat Capacity / Paid-user Cap */}
      <div className="bg-gray-800 rounded-lg p-6" data-testid="admin-capacity-section">
        <h2 className="text-xl font-semibold mb-1 text-white">Seat Capacity (Paid Users)</h2>
        <p className="text-sm text-gray-400 mb-4">
          Each paid user runs a live bot that consumes exchange API budget. When the
          cap is reached, new subscriptions are blocked and prospects are routed to the
          waitlist. Existing paid users are never affected.
        </p>

        {/* Live counter */}
        {capacity && (
          <div className="mb-5" data-testid="admin-capacity-live">
            <div className="flex items-end justify-between mb-2">
              <div>
                <span className="text-3xl font-bold text-white" data-testid="admin-paid-count">
                  {capacity.paid_count}
                </span>
                <span className="text-gray-400 text-lg"> / {capacity.max_paid_users} seats</span>
              </div>
              <span className={`text-sm font-semibold ${capacity.is_full ? 'text-red-400' : 'text-green-400'}`}>
                {capacity.is_full ? 'FULL — waitlist active' : `${capacity.seats_remaining} left`}
              </span>
            </div>
            <div className="w-full h-2.5 bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${capacity.is_full ? 'bg-red-500' : 'bg-gradient-to-r from-blue-500 to-purple-500'}`}
                style={{ width: `${capacity.pct_filled}%` }}
              />
            </div>
          </div>
        )}

        {/* Enforce toggle */}
        <div className="flex items-center space-x-4 mb-4">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.capacity_cap_enabled}
              onChange={(e) => setSettings({ ...settings, capacity_cap_enabled: e.target.checked })}
              className="w-6 h-6 rounded"
              data-testid="admin-capacity-cap-toggle"
            />
            <span className="text-white">
              {settings.capacity_cap_enabled ? 'Cap enforced (block at limit)' : 'Cap NOT enforced (unlimited)'}
            </span>
          </label>
        </div>

        {/* Max seats input */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Maximum paid users
          </label>
          <input
            type="number"
            min="1"
            value={settings.max_paid_users}
            onChange={(e) => setSettings({ ...settings, max_paid_users: e.target.value })}
            className="w-40 px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-blue-500"
            data-testid="admin-max-paid-users-input"
          />
          <p className="text-xs text-gray-500 mt-2">
            Tip: start conservative and raise it as you add capacity and watch the 429 rate.
          </p>
        </div>
      </div>

      {/* Banner Control */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4 text-white">Announcement Banner</h2>
        
        <div className="space-y-4">
          {/* Enable/Disable */}
          <div className="flex items-center space-x-3">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.banner.enabled}
                onChange={(e) => setSettings({
                  ...settings,
                  banner: { ...settings.banner, enabled: e.target.checked }
                })}
                className="w-6 h-6 rounded"
              />
              <span className="text-white font-semibold">Enable Banner</span>
            </label>
          </div>

          {/* Banner Text */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Banner Text
            </label>
            <input
              type="text"
              value={settings.banner.text}
              onChange={(e) => setSettings({
                ...settings,
                banner: { ...settings.banner, text: e.target.value }
              })}
              placeholder="Enter announcement text..."
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Display Location
            </label>
            <select
              value={settings.banner.location}
              onChange={(e) => setSettings({
                ...settings,
                banner: { ...settings.banner, location: e.target.value }
              })}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-blue-500"
            >
              <option value="off">Off</option>
              <option value="home">Home Page Only</option>
              <option value="dashboard">User Dashboard Only</option>
              <option value="everywhere">Everywhere</option>
            </select>
          </div>

          {/* Banner Type */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Banner Type
            </label>
            <select
              value={settings.banner.type}
              onChange={(e) => setSettings({
                ...settings,
                banner: { ...settings.banner, type: e.target.value }
              })}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-blue-500"
            >
              <option value="info">Info (Blue)</option>
              <option value="warning">Warning (Yellow)</option>
              <option value="success">Success (Green)</option>
            </select>
          </div>

          {/* Preview */}
          {settings.banner.enabled && settings.banner.text && (
            <div className="mt-4">
              <p className="text-sm text-gray-400 mb-2">Preview:</p>
              <div className={`p-3 rounded ${
                settings.banner.type === 'info' ? 'bg-blue-600' :
                settings.banner.type === 'warning' ? 'bg-yellow-600' :
                'bg-green-600'
              } text-white overflow-hidden`}>
                <div className="animate-marquee whitespace-nowrap inline-block">
                  {settings.banner.text}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="admin-save-settings-button"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default AdminSettings;